import { useEffect, useState } from "preact/hooks"
import { api } from "../lib/api"
import { thumbUrl } from "../lib/cloudinary"
import type { BoxSummary } from "../lib/types"
import { Spinner } from "./Spinner"

export function Home({
	cloudName,
	navigate,
}: {
	cloudName: string
	navigate: (path: string) => void
}) {
	const [boxes, setBoxes] = useState<BoxSummary[] | null>(null)
	const [error, setError] = useState("")
	const [creating, setCreating] = useState(false)
	const [name, setName] = useState("")
	const [saving, setSaving] = useState(false)

	const load = () => {
		api
			.listBoxes()
			.then(setBoxes)
			.catch((err) =>
				setError(err instanceof Error ? err.message : "Failed to load boxes"),
			)
	}

	useEffect(load, [])

	const createBox = async (e: Event) => {
		e.preventDefault()
		if (!name.trim()) return
		setSaving(true)
		setError("")
		try {
			const box = await api.createBox(name.trim())
			setName("")
			setCreating(false)
			navigate(`/boxes/${box.id}`)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to create box")
		} finally {
			setSaving(false)
		}
	}

	return (
		<div class="container py-4">
			<div class="d-flex align-items-center justify-content-between mb-3">
				<h1 class="h4 mb-0">Your boxes</h1>
				<button
					type="button"
					class="btn btn-primary"
					onClick={() => setCreating(true)}
				>
					<i class="bi bi-plus-lg" aria-hidden="true" />
					<span>New box</span>
				</button>
			</div>

			{creating && (
				<>
					<div class="modal fade show d-block" role="dialog" aria-modal="true">
						<div class="modal-dialog">
							<form class="modal-content" onSubmit={createBox}>
								<div class="modal-header">
									<h2 class="modal-title fs-5">New box</h2>
									<button
										type="button"
										class="btn-close"
										aria-label="Close"
										onClick={() => setCreating(false)}
									/>
								</div>
								<div class="modal-body">
									<input
										type="text"
										class="form-control"
										placeholder="Kid's name, e.g. Maya"
										value={name}
										onInput={(e) =>
											setName((e.target as HTMLInputElement).value)
										}
										autoFocus
										required
									/>
								</div>
								<div class="modal-footer">
									<button
										type="button"
										class="btn btn-outline-secondary"
										onClick={() => setCreating(false)}
									>
										Cancel
									</button>
									<button
										type="submit"
										class="btn btn-primary"
										disabled={saving}
									>
										{saving ? (
											<Spinner size={16} />
										) : (
											<i class="bi bi-check-lg" aria-hidden="true" />
										)}
										<span>Create</span>
									</button>
								</div>
							</form>
						</div>
					</div>
					<div
						class="modal-backdrop fade show"
						onClick={() => setCreating(false)}
					/>
				</>
			)}

			{error && <div class="alert alert-danger">{error}</div>}

			{boxes === null ? (
				<Spinner />
			) : boxes.length === 0 ? (
				<p class="text-body-secondary">
					No boxes yet. Create one to get started.
				</p>
			) : (
				<div class="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-3">
					{boxes.map((box) => (
						<div class="col" key={box.id}>
							<a
								href={`/boxes/${box.id}`}
								class="card text-decoration-none h-100"
							>
								<div class="ratio ratio-1x1 bg-body-secondary rounded-top overflow-hidden">
									{box.cover_public_id && cloudName ? (
										<img
											src={thumbUrl(cloudName, box.cover_public_id)}
											alt=""
											class="w-100 h-100 object-fit-cover"
										/>
									) : (
										<div class="d-flex align-items-center justify-content-center">
											<i
												class="bi bi-images fs-1 text-body-secondary"
												aria-hidden="true"
											/>
										</div>
									)}
								</div>
								<div class="card-body py-2 px-2">
									<div class="d-flex align-items-center justify-content-between gap-1">
										<span class="fw-semibold text-body text-truncate">
											{box.name}
										</span>
										{box.role === "owner" && (
											<span class="badge text-bg-accent flex-shrink-0">
												owner
											</span>
										)}
									</div>
									<div class="small text-body-secondary">
										{box.artwork_count}{" "}
										{box.artwork_count === 1 ? "piece" : "pieces"}
									</div>
								</div>
							</a>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
